import FileSender from './fileSender';
import FileReceiver from './fileReceiver';
import { copyToClipboard, delay, openLinksInNewTab, percent } from './utils';
import * as metrics from './metrics';
import { bytes, locale } from './utils';
import okDialog from './ui/okDialog';
import copyDialog from './ui/copyDialog';
import resendSMSDialog from './ui/resendSMSDialog';
import { sendTextMessage } from './api';
import shareDialog from './ui/shareDialog';
import signupDialog from './ui/signupDialog';
import surveyDialog from './ui/surveyDialog';
import { validateSD } from './validationFE';
import SyndisData from './syndisData';
import taktikalStart, { taktikalSignin } from './taktikalSSN';
import { taktikalPoll } from './taktikalSSN';
import { generate } from './generate-password';

export default function (state, emitter) {
  let lastRender = 0;
  let updateTitle = false;

  function render() {
    emitter.emit('render');
  }

  async function checkFiles() {
    const changes = await state.user.syncFileList();
    const rerender = changes.incoming || changes.downloadCount;
    if (rerender) {
      render();
    }
  }

  function updateProgress() {
    if (updateTitle) {
      emitter.emit('DOMTitleChange', percent(state.transfer.progressRatio));
    }
    render();
  }

  emitter.on('DOMContentLoaded', () => {
    document.addEventListener('blur-sm', () => (updateTitle = true));
    document.addEventListener('focus', () => {
      updateTitle = false;
      emitter.emit('DOMTitleChange', 'Syndis Share');
    });
    checkFiles();
  });

  emitter.on('render', () => {
    lastRender = Date.now();
  });

  emitter.on('login', (email) => {
    state.user.login(email);
  });

  emitter.on('logout', () => {
    state.user.logout();
    metrics.loggedOut({ trigger: 'button' });
    emitter.emit('pushState', '/');
  });

  emitter.on('removeUpload', (file) => {
    state.archive.remove(file);
    if (state.archive.numFiles === 0) {
      state.archive.clear();
    }
    render();
  });

  emitter.on('delete', async (ownedFile) => {
    try {
      metrics.deletedUpload({
        size: ownedFile.size,
        time: ownedFile.time,
        speed: ownedFile.speed,
        type: ownedFile.type,
        ttl: ownedFile.expiresAt - Date.now(),
        location,
      });
      state.storage.remove(ownedFile.id);
      await ownedFile.del();
    } catch (e) {
      state.sentry.captureException(e);
    }
    render();
  });

  emitter.on('cancel', () => {
    state.transfer.cancel();
  });

  emitter.on('addFiles', async ({ files }) => {
    if (files.length < 1) {
      return;
    }
    const maxSize = state.user.maxSize;
    try {
      state.archive.addFiles(
        files,
        maxSize,
        state.LIMITS.MAX_FILES_PER_ARCHIVE,
      );
    } catch (e) {
      if (e.message === 'fileTooBig' && maxSize < state.LIMITS.MAX_FILE_SIZE) {
        return emitter.emit('signup-cta', 'size');
      }
      state.modal = okDialog(
        state.translate(e.message, {
          size: bytes(maxSize),
          count: state.LIMITS.MAX_FILES_PER_ARCHIVE,
        }),
      );
    }
    render();
  });

  emitter.on('signup-cta', (source) => {
    const query = state.query;
    state.user.startAuthFlow(source, {
      campaign: query.utm_campaign,
      content: query.utm_content,
      medium: query.utm_medium,
      source: query.utm_source,
      term: query.utm_term,
    });
    state.modal = signupDialog(source);
    render();
  });

  emitter.on('authenticate', async (code, oauthState) => {
    try {
      await state.user.finishLogin(code, oauthState);
      await state.user.syncFileList();
      emitter.emit('replaceState', '/');
    } catch (e) {
      emitter.emit('replaceState', '/error');
      setTimeout(render);
    }
  });

  emitter.on('upload', async () => {
    if (!validateSD(state)) {
      return render();
    }

    if (state.storage.files.length >= state.LIMITS.MAX_ARCHIVES_PER_USER) {
      state.modal = okDialog(
        state.translate('tooManyArchives', {
          count: state.LIMITS.MAX_ARCHIVES_PER_USER,
        }),
      );
      return render();
    }

    const archive = state.archive;
    const sender = new FileSender();
    const syndisData = new SyndisData(
      state.syndisData.emailList,
      state.syndisData.phoneList,
      state.syndisData.ssnList,
      state.syndisData.description,
    );

    if (
      state.syndisInputs.useProtection &&
      state.syndisInputs.usePwd &&
      state.syndisInputs.randomPwd
    ) {
      archive.password = generate({
        length: 10,
        numbers: true,
        excludeSimilarCharacters: true,
      });
    }

    sender.on('progress', updateProgress);
    sender.on('encrypting', render);
    sender.on('complete', render);
    state.transfer = sender;
    state.uploading = true;
    render();

    const links = openLinksInNewTab();
    await delay(200);
    const start = Date.now();
    try {
      const ownedFile = await sender.upload(
        archive,
        syndisData,
        state.user.bearerToken,
      );
      state.storage.totalUploads += 1;

      const duration = Date.now() - start;
      metrics.completedUpload(archive, duration);

      state.storage.addFile(ownedFile);
      // TODO integrate password into /upload request
      if (
        state.syndisInputs.useProtection &&
        state.syndisInputs.usePwd &&
        archive.password
      ) {
        emitter.emit('password', {
          password: archive.password,
          file: ownedFile,
        });
      }
      const nextModal = state.capabilities.share
        ? shareDialog(ownedFile.name, ownedFile.url)
        : copyDialog(ownedFile.name, ownedFile.url);
      state.modal = nextModal;

      const hasPhone = !(
        syndisData.phoneList.length === 1 && syndisData.phoneList[0] === ''
      );

      if (hasPhone) {
        const failedToSendList = await sendTextMessage(
          syndisData.phoneList,
          archive.password,
        );

        if (failedToSendList.length > 0 && failedToSendList[0] !== '') {
          state.modal = resendSMSDialog();
          state.nextModal = nextModal;
          state.modal.phoneList = failedToSendList;
          state.modal.showPwd = false;
          state.modal.password = archive.password;
        }
      }
    } catch (err) {
      if (err.message === '0') {
        //cancelled. do nothing
        metrics.cancelledUpload(archive, err.duration);
        render();
      } else {
        // eslint-disable-next-line no-console
        console.error(err);
        state.sentry.withScope((scope) => {
          scope.setExtra('duration', err.duration);
          scope.setExtra('size', err.size);
          state.sentry.captureException(err);
        });
        metrics.stoppedUpload(archive, err.duration);
        emitter.emit('pushState', '/error');
      }
    } finally {
      if (state.syndisData.emailList) {
        localStorage.setItem('emailList', state.syndisData.emailList);
      }
      openLinksInNewTab(links, false);
      archive.clear();
      state.syndisData.clear();
      clearSyndisInputs(state);
      state.uploading = false;
      state.transfer = null;
      await state.user.syncFileList();
      render();
    }
  });

  emitter.on('password', async ({ password, file }) => {
    try {
      state.settingPassword = true;
      render();
      await file.setPassword(password);
      state.storage.writeFile(file);
      await delay(1000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      state.passwordSetError = err;
    } finally {
      state.settingPassword = false;
    }
    render();
  });

  emitter.on('getMetadata', async () => {
    const file = state.fileInfo;
    const receiver = new FileReceiver(file);
    try {
      await receiver.getMetadata();
      state.transfer = receiver;
    } catch (e) {
      if (e.message === '401' || e.message === '404') {
        file.password = null;
        if (!file.requiresPassword) {
          return emitter.emit('pushState', '/404');
        }
      }
    }
    render();
  });

  emitter.on('getAllMeta', async () => {
    const file = state.fileInfo;
    const receiver = new FileReceiver(file);
    try {
      await receiver.getAllMeta();
      state.transfer = receiver;
    } catch (e) {
      if (e.message === '401' || e.message === '404') {
        file.password = null;
        if (!file.requiresPassword) {
          return emitter.emit('pushState', '/404');
        }
      }
    }

    render();
  });

  emitter.on('getOwner', async () => {
    const file = state.fileInfo;

    const receiver = new FileReceiver(file);
    try {
      await receiver.getOwner();
    } catch (e) {
      if (e.message === '401' || e.message === '404') {
        file.password = null;
        if (!file.requiresPassword) {
          return emitter.emit('pushState', '/404');
        }
      }
    }

    render();
  });

  emitter.on('download', async (file) => {
    state.transfer.on('progress', updateProgress);
    state.transfer.on('decrypting', render);
    state.transfer.on('complete', render);
    const links = openLinksInNewTab();
    const size = file.size;
    const start = Date.now();

    try {
      const dl = state.transfer.download({
        stream: state.capabilities.streamDownload,
      });
      render();
      await dl;
      state.storage.totalDownloads += 1;
      const duration = Date.now() - start;
      metrics.completedDownload({
        size,
        duration,
        password_protected: file.requiresPassword,
      });
    } catch (err) {
      if (err.message === '0') {
        state.transfer.reset();
        render();
      } else if (
        err.name === 'AbortError' ||
        err.code === 'ERR_STREAM_PREMATURE_CLOSE'
      ) {
        // pause: do nothing, leave progress intact
      } else {
        state.transfer = null;
        const location = err.message === '404' ? '/404' : '/error';
        if (location === '/error') {
          state.sentry.withScope((scope) => {
            scope.setExtra('duration', err.duration);
            scope.setExtra('size', err.size);
            scope.setExtra('progress', err.progress);
            state.sentry.captureException(err);
          });
          const duration = Date.now() - start;
          metrics.stoppedDownload({
            size,
            duration,
            password_protected: file.requiresPassword,
          });
        }
        emitter.emit('pushState', location);
      }
    } finally {
      openLinksInNewTab(links, false);
    }
  });

  emitter.on('copy', ({ url }) => {
    copyToClipboard(url);
    // metrics.copiedLink({ location });
  });

  emitter.on('closeModal', () => {
    if (
      state.PREFS.surveyUrl &&
      ['copy', 'share'].includes(state.modal.type) &&
      locale().startsWith('en') &&
      (state.storage.totalUploads > 1 || state.storage.totalDownloads > 0) &&
      !state.user.surveyed
    ) {
      state.user.surveyed = true;
      state.modal = surveyDialog();
    } else {
      state.modal = null;
    }
    render();
  });

  emitter.on('switchModal', () => {
    render();
  });

  emitter.on('taktikalAuth', async ({ phone, id }) => {
    try {
      const start = await taktikalStart(phone, id);

      if (start.authRequestId) {
        state.taktikalStart = start;
        render();
        const response = await taktikalPoll(start.authRequestId, id);
        state.taktikalResponse = response.status;
        render();

        if (response.redirected) {
          emitter.emit('pushState', response.url);
        }
      } else if (start.status === 403) {
        state.taktikalStart = 'Forbidden input 403';
        console.error('authentication input error');
        render();
      } else {
        state.taktikalStart = `Error starting auth (status ${start.status})`;
        console.error('authentication error');
        render();
      }
    } catch (err) {
      console.error('controller error', err);
    }
  });

  emitter.on('taktikalSignin', async ({ phone }) => {
    try {
      const start = await taktikalStart(phone);

      if (start.authRequestId) {
        state.taktikalStart = start;
        render();
        const response = await taktikalSignin(start.authRequestId);
        state.taktikalResponse = response.status;
        render();

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        state.taktikalUser.name = data.name;
        state.taktikalUser.ssn = data.ssn;
        render();
      } else if (start.status === 403) {
        state.taktikalStart = 'Forbidden input 403';
        console.error('authentication input error');
        render();
      } else {
        state.taktikalStart = `Error starting auth (status ${start.status})`;
        console.error('authentication error');
        render();
      }
    } catch (err) {
      console.error('controller error', err);
    }
  });

  setInterval(
    () => {
      // poll for updates of the upload list
      if (!state.modal && state.route === '/') {
        checkFiles();
      }
    },
    2 * 60 * 1000,
  );

  setInterval(() => {
    // poll for rerendering the file list countdown timers
    if (
      !state.modal &&
      state.route === '/' &&
      state.storage.files.length > 0 &&
      Date.now() - lastRender > 30000
    ) {
      render();
    }
  }, 60000);
}

function clearSyndisInputs(state) {
  state.syndisInputs = {
    emailInput: '',
    phoneInput: '',
    ssnInput: '',
    descriptionInput: '',
    emailError: false,
    phoneError: false,
    ssnError: false,
    pwdError: false,
    useProtection: false,
    usePwd: true,
    useSSN: false,
    randomPwd: false,
    taktikalPhone: '',
  };
}
